import {
  Button,
  Stack,
  Typography,
  Link,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import React, { useRef } from "react";
import { InputControl, PageListLayout } from "../../../shared";
import "react-quill/dist/quill.snow.css";
import { useNavigate, useParams } from "react-router-dom";
import { useTipsAndTricks } from "../hooks/useTipsAndTricks";
import ReactQuill from "react-quill";
import StepsSection from "../components/StepsSection";
import FileUploadSection from "../components/FileUploadSection";
import RecipeTaggingSection from "../components/RecipeTaggingSection";
import { CategoryTaggingSection } from "../components";

const FormSection = ({ children, title }) => {
  return (
    <Stack
      sx={{
        display: "grid",
        gridTemplateColumns: { md: "1fr 2fr", xs: "1fr" },
        flexDirection: { md: "row", xs: "column" },
        borderBottom: 1,
        borderColor: "divider",
        py: "24px",
        rowGap: { xs: "12px", md: "0" },
      }}
    >
      <Typography variant="subtitle1" fontWeight={600}>
        {title}
      </Typography>
      <Stack>{children}</Stack>
    </Stack>
  );
};

export const AddorEditTipsAndTricks = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    tipsAndTricks,
    onChange,
    validator,
    recipeListQuery,
    onSubmit,
    onDeleteRecipeSelect,
    handleInputChangeFile,
    onDeleteFileSelect,
    updateTipsAndTricks,
    createTipsAndTricks,
    createImageFileUrl,
    typeRadioButtonChange,
    handleQuillChange,
    handleDragEnd,
    handleDropzone,
    addStepChangeHandler,
    removeImage,
    handleStepChange,
    stepsHandleDelete,
    CategoryOptions,
    onDeleteCategorySelect,
  } = useTipsAndTricks({
    isList: false,
    tipId: id,
  });

  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "list",
    "bullet",
    "indent",
    "link",
    "image",
    "color",
  ];
  const modules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ["bold", "italic", "underline"],
      [
        { list: "ordered" },
        { list: "bullet" },
        { indent: "-1" },
        { indent: "+1" },
      ],
      [
        {
          color: [],
        },
      ],
    ],
  };
  const videoRef = useRef();

  const RecipeOptions = recipeListQuery?.data?.map((v) => {
    return { id: v.id, name: v.name };
  });

  const breadcrumbs = [
    <Link
      underline="none"
      key="1"
      color="#1760D2"
      onClick={(e) => navigate("/tips-tricks")}
      sx={{ fontSize: "14px", fontWeight: 400 }}
    >
      Tips & Procedures
    </Link>,
    <Typography
      key="2"
      color="inherit"
      sx={{ fontSize: "14px", fontWeight: 400 }}
    >
      {id
        ? tipsAndTricks?.data?.name
        : tipsAndTricks?.data?.type === "Tips & Tricks"
          ? "Create New Tip"
          : "Create New Procedure"}
    </Typography>,
  ];

  return (
    <>
      {validator.current.purgeFields()}
      <PageListLayout
        breadcrumbs={breadcrumbs}
        headerText={
          id
            ? tipsAndTricks?.data?.name
            : tipsAndTricks?.data?.type === "Tips & Tricks"
              ? "Create New Tip"
              : "Create New Procedure"
        }
        rightContent={
          <Stack direction="row" gap="8px">
            <Button
              type="button"
              size="small"
              variant="contained"
              color="inherit"
              onClick={() => navigate(-1)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="small"
              variant="contained"
              onClick={onSubmit}
              disabled={
                updateTipsAndTricks.isPending || createTipsAndTricks.isPending
              }
            >
              {id ? "Update" : "Save"}
            </Button>
          </Stack>
        }
        sx={{ px: "16px", pt: 0, backgroundColor: "#ffffff" }}
      >
        <FormSection title="Select Type*">
          <Stack direction="row" justifyContent={"space-between"}>
            <RadioGroup row>
              <FormControlLabel
                control={
                  <Radio
                    checked={tipsAndTricks.data.type === "Tips & Tricks"}
                  />
                }
                label="Tips & Tricks"
                name="primary"
                disabled={id ? true : false}
                value={tipsAndTricks.data.type}
                onChange={(event) => typeRadioButtonChange("Tips & Tricks")}
              />
              <FormControlLabel
                control={
                  <Radio checked={tipsAndTricks.data.type === "Procedures"} />
                }
                disabled={id ? true : false}
                label="Procedures"
                name="primary"
                value={tipsAndTricks.data.type}
                onChange={(event) => typeRadioButtonChange("Procedures")}
              />
            </RadioGroup>
          </Stack>
        </FormSection>

        <FormSection title="Name*">
          <InputControl
            name="name"
            type="text"
            label="Name"
            required
            error={validator.current.message(
              "Name",
              tipsAndTricks?.data?.name,
              "required",
            )}
            helperText={validator.current.message(
              "Name",
              tipsAndTricks?.data?.name,
              "required",
            )}
            onChange={onChange}
            value={tipsAndTricks?.data?.name}
          />
        </FormSection>

        <FormSection title="Method">
          <ReactQuill
            style={{ display: "contents" }}
            theme="snow"
            name="method"
            modules={modules}
            formats={formats}
            value={tipsAndTricks?.data?.method}
            onChange={(value) => handleQuillChange(value)}
          />
        </FormSection>

        <FormSection title="How to Prepare">
          <StepsSection
            tipsAndTricks={tipsAndTricks}
            validator={validator}
            handleDragEnd={handleDragEnd}
            handleDropzone={handleDropzone}
            removeImage={removeImage}
            handleStepChange={handleStepChange}
            stepsHandleDelete={stepsHandleDelete}
            addStepChangeHandler={addStepChangeHandler}
            modules={modules}
            formats={formats}
          />
        </FormSection>

        <FormSection title="Files">
          <FileUploadSection
            tipsAndTricks={tipsAndTricks}
            validator={validator}
            handleInputChangeFile={handleInputChangeFile}
            onDeleteFileSelect={onDeleteFileSelect}
            createImageFileUrl={createImageFileUrl}
            videoRef={videoRef}
          />
        </FormSection>

        <FormSection title="Tag Recipe?">
          <RecipeTaggingSection
            tipsAndTricks={tipsAndTricks}
            onChange={onChange}
            onDeleteRecipeSelect={onDeleteRecipeSelect}
            RecipeOptions={RecipeOptions}
          />
        </FormSection>

        <FormSection title="Tag Category?">
          <CategoryTaggingSection
            tipsAndTricks={tipsAndTricks}
            onChange={onChange}
            onDeleteRecipeSelect={onDeleteRecipeSelect}
            CategoryOptions={CategoryOptions}
            onDeleteCategorySelect={onDeleteCategorySelect}
          />
        </FormSection>
      </PageListLayout>
    </>
  );
};
